import type { HandleMessageAutomationOptions } from "../apis/chatApi";
import type { Agent } from "../types/integration";

export type TicketChatAutomationMode = 0 | 3;

/**
 * What a ticket chat runs as: plain chat, or Auto Resolver as one of the MSP's agents.
 * `agentId` is null in plain chat, and for an Auto Resolver run whose agent is not known yet.
 */
export interface TicketChatMode {
  capacity: TicketChatAutomationMode;
  agentId: number | null;
}

export const NORMAL_CHAT_MODE: TicketChatMode = Object.freeze({ capacity: 0, agentId: null });

export function threadChatMode(capacity: TicketChatAutomationMode, agentId: number | null): TicketChatMode {
  return capacity === 3 ? { capacity: 3, agentId: agentId || null } : NORMAL_CHAT_MODE;
}

export function sameTicketChatMode(left: TicketChatMode, right: TicketChatMode): boolean {
  return left.capacity === right.capacity && left.agentId === right.agentId;
}

/** The agents the mode selector offers: enabled ones, the default Theo agent first. */
export function selectableAgents(agents: readonly Agent[]): Agent[] {
  const enabled = agents.filter((agent) => agent.enabled);
  return [...enabled.filter((agent) => agent.is_default), ...enabled.filter((agent) => !agent.is_default)];
}

export function canShowTicketChatModeSelector(ticketId: string | null | undefined): boolean {
  return Boolean(ticketId);
}

/** An explicit mode for the next message. A picked agent is named by ID; the server loads its config. */
export function ticketChatModeSendOptions(mode: TicketChatMode): { automation: HandleMessageAutomationOptions } {
  const automation: HandleMessageAutomationOptions = { automation_capacity: mode.capacity, automation_override: true };
  return { automation: mode.capacity === 3 && mode.agentId ? { ...automation, agent_id: mode.agentId } : automation };
}
